/**
 * #1212 texting B2a — the desk's calls for a client's texting consent.
 * Kept out of lib/api.ts (a file the parallel texting-B1 lane also edits).
 *
 * Staff can READ the record and RECORD AN OPT-OUT. There is no call here that
 * grants, because there is no staff route that can: only the client can say
 * yes to texts (#682 decision of record).
 */
import { request, type ApiResponse } from './api.js';

export type SmsConsentKind = 'transactional' | 'marketing';

export type SmsConsentSource =
  | 'signup'
  | 'signin_prompt'
  | 'customer_settings'
  | 'staff_recorded_optout'
  | 'carrier_optout'
  | 'staff_self';

export type SmsConsentStatus =
  | { status: 'none' }
  | { status: 'granted' | 'revoked'; at: string; source: SmsConsentSource };

export interface SmsConsentHistoryEntry {
  id: string;
  kind: SmsConsentKind;
  action: 'granted' | 'revoked';
  source: SmsConsentSource;
  phone_last4: string;
  at: string;
  disclosure_version: string | null;
  recorded_by_staff: boolean;
}

export interface StaffSmsConsentView {
  phone_last4: string | null;
  transactional: SmsConsentStatus;
  marketing: SmsConsentStatus;
  history: SmsConsentHistoryEntry[];
}

export function fetchCustomerSmsConsent(
  apiUrl: string,
  customerId: string,
): Promise<ApiResponse<StaffSmsConsentView>> {
  return request<StaffSmsConsentView>(
    apiUrl,
    `/api/admin/customers/${encodeURIComponent(customerId)}/sms-consent`,
  );
}

export function recordCustomerSmsOptOut(
  apiUrl: string,
  customerId: string,
  kind: SmsConsentKind,
): Promise<ApiResponse<StaffSmsConsentView>> {
  return request<StaffSmsConsentView>(
    apiUrl,
    `/api/admin/customers/${encodeURIComponent(customerId)}/sms-consent/opt-out`,
    { method: 'POST', body: JSON.stringify({ kind }) },
  );
}

/** How the answer was given, in the desk's words. */
export function describeSource(source: SmsConsentSource): string {
  switch (source) {
    case 'signup':
      return 'when they created their account';
    case 'signin_prompt':
      return 'when they signed in';
    case 'customer_settings':
      return 'in their app';
    case 'staff_recorded_optout':
      return 'at the desk, recorded by staff';
    case 'carrier_optout':
      return 'by replying STOP';
    case 'staff_self':
      return 'by the staff member themselves';
  }
}
